/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps } from '@msdyn365-commerce/core';
import React from 'react';

import { BusinessPartner, BusinessPartnersDataActions } from '@msdyn365-commerce/retail-proxy';
import { IBusinessPartnersResources } from '../business-partners.props.autogenerated';
import { DataGrid, IDataGridColumn } from './data-grid';

/**
 * Props of IBusinessPartnersTable.
 */
export interface IBusinessPartnersTableProps extends IComponentProps {
    className: string;
    pageSize?: number;
    resources: IBusinessPartnersResources;
    onSelect(businessPartner: BusinessPartner): void;
}

/**
 * BusinessPartnersTable component.
 */
export class BusinessPartnersTable extends React.Component<IBusinessPartnersTableProps> {
    public shouldComponentUpdate(nextProps: Readonly<IBusinessPartnersTableProps>): boolean {
        return this.props.id !== nextProps.id;
    }

    public render(): JSX.Element {
        return (
            <DataGrid
                {...this.props}
                loadData={this.getBusinessPartners}
                columns={this.getColumns()}
                defaultSorting={{ fieldName: 'Name', ascending: true }}
                searchPlaceholderLabel={this.props.resources.businessPartnerSearchCompany}
            />
        );
    }

    /**
     * Get column definition of the table.
     * @returns - Columns.
     */
    private readonly getColumns = (): IDataGridColumn<BusinessPartner>[] => {
        return [
            {
                reactKey: 'name',
                name: this.props.resources.businessPartnerName,
                isSearchable: true,
                fieldName: 'Name',
                onClick: this.props.onSelect,
                onRender: (data: BusinessPartner) => <a role='button'>{data.Name}</a>
            }
        ];
    };

    /**
     * Call retail proxy to get business partners.
     * @returns - Business partners.
     */
    private readonly getBusinessPartners = async () => {
        const businessPartners = await BusinessPartnersDataActions.getManagedBusinessPartnersAsync({
            callerContext: this.props.context.actionContext,
            bypassCache: 'get',
            queryResultSettings: {}
        });
        return businessPartners;
    };
}
